import { Box, Typography, useTheme } from "@mui/material";
import { Warning20Px, Info20Px } from "@qubit/autoparts";
import type { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

import { settingsOverviewHelperSx } from "~/features/settings/settings.styles";
import { WorkstationSummaryDto } from "~/types/api";

type Props = {
  /** Workstation assigned to this device. */
  siteWorkstation?: WorkstationSummaryDto | null;
  /** Workstation currently selected in the assign dropdown. */
  dropdownWorkstation?: WorkstationSummaryDto | null;
};

type HelperSeverity = "none" | "warning" | "info";

type HelperState = {
  content: ReactNode;
  severity: HelperSeverity;
};

const boldComponents = {
  bold: <Typography component="span" fontSize="inherit" fontWeight="bold" />
};

function HelperSeverityIcon({ severity }: { severity: "warning" | "info" }) {
  const theme = useTheme();
  const Icon = severity === "warning" ? Warning20Px : Info20Px;
  const fill =
    severity === "warning"
      ? theme.palette.warning.main
      : theme.palette.info.main;

  return (
    <Icon
      role="img"
      aria-label={severity === "warning" ? "Warning" : "Info"}
      fill={fill}
      style={{
        width: 16,
        height: 16,
        marginTop: "1px",
        flexShrink: 0
      }}
    />
  );
}

export function WorkstationOverviewHelperText({
  siteWorkstation,
  dropdownWorkstation
}: Props) {
  const { t } = useTranslation();

  const isMatched =
    Boolean(dropdownWorkstation) &&
    dropdownWorkstation?.id === siteWorkstation?.id;

  if (isMatched) {
    return null;
  }

  const getHelperState = (): HelperState => {
    if (!dropdownWorkstation) {
      if (siteWorkstation) {
        return {
          severity: "info",
          content: (
            <Trans
              i18nKey="workstation editing assigned empty helper"
              values={{ assigned: siteWorkstation.deviceId }}
              components={boldComponents}
            />
          )
        };
      }
      return {
        severity: "none",
        content: t("select workstation id matches port helper")
      };
    }

    if (!siteWorkstation) {
      if (dropdownWorkstation.status === "Active") {
        return {
          severity: "warning",
          content: (
            <Trans
              i18nKey="workstation editing unassigned in use helper"
              values={{ editing: dropdownWorkstation.deviceId }}
              components={boldComponents}
            />
          )
        };
      }
      return {
        severity: "warning",
        content: t("workstation editing unassigned device helper")
      };
    }

    return {
      severity: "warning",
      content: (
        <Trans
          i18nKey="workstation editing assigned idle helper"
          values={{
            editing: dropdownWorkstation.deviceId,
            assigned: siteWorkstation.deviceId
          }}
          components={boldComponents}
        />
      )
    };
  };

  const { content, severity } = getHelperState();

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 0.5 }}>
      {(severity === "warning" || severity === "info") && (
        <HelperSeverityIcon severity={severity} />
      )}
      <Typography
        id="ws-site-assignment-helper"
        variant="body2"
        sx={[settingsOverviewHelperSx, { color: "text.primary" }]}
      >
        {content}
      </Typography>
    </Box>
  );
}
